import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { salaryAPI, organizationAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  RefreshCw, UserX, Banknote, Trash2, AlertTriangle, CheckCircle, XCircle, Calendar, Clock, Ban, ChevronDown,
} from 'lucide-react';
import ITEditForm from '@/components/salary/ITEditForm';
import { SettlementComments } from '@/components/salary/SettlementComments';
import { SettlementHistory } from '@/components/salary/SettlementHistory';

const fmt = (amount) => {
  if (amount == null || isNaN(amount)) return '0.00';
  return Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

export default function ResignedSalaryPage() {
  const { canEditLOP, canFinalize, canMarkPaid, isAdminLevel, canEditFinalized } = useAuth();

  const [records, setRecords] = useState([]);
  const [prevMonthData, setPrevMonthData] = useState({});
  const [noticeSettlement, setNoticeSettlement] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterOrg, setFilterOrg] = useState('all');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type, id, label }
  const [activeTab, setActiveTab] = useState('active');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month: selectedMonth, year: selectedYear, is_resigned: true };
      if (filterOrg !== 'all') params.organization_id = filterOrg;
      const response = await salaryAPI.getAll(params);
      const recs = response.data || [];
      setRecords(recs);

      // Load previous month data
      if (recs.length > 0) {
        const empIds = recs.map(r => r.employee_id).join(',');
        try {
          const prevRes = await salaryAPI.getPreviousMonth(empIds, selectedMonth, selectedYear);
          setPrevMonthData(prevRes.data || {});
        } catch { setPrevMonthData({}); }

        // Load notice settlement totals for each employee
        const settlements = {};
        await Promise.all(recs.map(async (r) => {
          try {
            const res = await salaryAPI.getNoticeSettlement(r.employee_id);
            settlements[r.employee_id] = res.data;
          } catch { /* skip */ }
        }));
        setNoticeSettlement(settlements);
      }
    } catch {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, filterOrg]);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => {
    organizationAPI.getAll().then(r => setOrganizations(r.data || [])).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    try {
      const orgId = filterOrg !== 'all' ? filterOrg : undefined;
      const res = await salaryAPI.generateResigned(selectedMonth, selectedYear, orgId);
      toast.success(res.data?.message || 'Generated');
      setGenerateDialogOpen(false);
      loadRecords();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate'); }
  };

  const executeAction = async () => {
    if (!confirmAction) return;
    const { type, id } = confirmAction;
    try {
      if (type === 'finalize') await salaryAPI.finalize(id);
      else if (type === 'unfinalize') await salaryAPI.unfinalize(id);
      else if (type === 'paid') await salaryAPI.markPaid(id);
      else if (type === 'reverse') await salaryAPI.reverse(id);
      else if (type === 'delete') await salaryAPI.delete(id);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} successful`);
      setConfirmAction(null);
      loadRecords();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
  const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="resigned-salary-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <UserX className="h-6 w-6 text-orange-500" />
              Resigned Employees — Settlement
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Final salary & settlement for employees on notice period</p>
          </div>
          {activeTab === 'active' && isAdminLevel() && (
            <Button variant="outline" onClick={() => setGenerateDialogOpen(true)} data-testid="generate-resigned-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Generate Monthly
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b" data-testid="settlement-tabs">
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px cursor-pointer ${activeTab === 'active' ? 'border-orange-500 text-orange-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('active')}
            data-testid="tab-active-settlements"
          >
            Active Settlements
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px cursor-pointer ${activeTab === 'history' ? 'border-orange-500 text-orange-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('history')}
            data-testid="tab-settlement-history"
          >
            Settlement History
          </button>
        </div>

        {activeTab === 'history' ? (
          <SettlementHistory />
        ) : (
        <>
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterOrg} onValueChange={setFilterOrg}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Organizations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
              {organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">Loading...</div>
        ) : records.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <UserX className="h-14 w-14 mb-3 opacity-30" />
              <p className="text-lg font-medium">No resigned employee records</p>
              <p className="text-sm">Generate monthly records for employees on notice.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {records.map(record => (
              <SettlementCard
                key={record.id}
                record={record}
                prevMonth={prevMonthData[record.employee_id]}
                noticeSettlement={noticeSettlement[record.employee_id]}
                prevMonthLabel={`${months[prevMonth - 1]?.label} ${prevYear}`}
                currentMonthLabel={`${months[selectedMonth - 1]?.label} ${selectedYear}`}
                canEdit={!record.is_finalized && canEditLOP()}
                canFinalize={canFinalize}
                canMarkPaid={canMarkPaid}
                canEditFinalized={canEditFinalized}
                isAdminLevel={isAdminLevel}
                onAction={(type) => setConfirmAction({ type, id: record.id, label: type })}
                onUpdate={loadRecords}
              />
            ))}
          </div>
        )}

        </>
        )}

        {/* Generate Dialog */}
        <AlertDialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Generate Resigned Employee Records</AlertDialogTitle>
              <AlertDialogDescription>
                Generate salary records for {months[selectedMonth - 1]?.label} {selectedYear} for all employees on notice. Days worked will be auto-calculated from their notice dates.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleGenerate} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-generate-resigned">Generate</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirmation Dialog */}
        <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmAction?.type === 'delete' ? 'Delete Record' :
                 confirmAction?.type === 'reverse' ? 'Reverse Payment' :
                 confirmAction?.type === 'unfinalize' ? 'Revert Finalization' :
                 confirmAction?.type === 'paid' ? 'Mark as Paid' :
                 'Finalize Record'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction?.type === 'delete' ? 'Permanently delete this settlement record?' :
                 confirmAction?.type === 'reverse' ? 'Reverse the payment? This will remove the Paid status.' :
                 confirmAction?.type === 'unfinalize' ? 'Revert to unfinalized state?' :
                 confirmAction?.type === 'paid' ? 'Mark this settlement as paid?' :
                 'Finalize this settlement? This locks it from further edits.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={executeAction}
                className={confirmAction?.type === 'delete' || confirmAction?.type === 'reverse' ? 'bg-red-600 hover:bg-red-700' : confirmAction?.type === 'paid' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                data-testid={`confirm-${confirmAction?.type}-resigned`}
              >
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}

// ==================== Settlement Card Component ====================
function SettlementCard({ record, prevMonth, noticeSettlement, prevMonthLabel, currentMonthLabel, canEdit, canFinalize, canMarkPaid, canEditFinalized, isAdminLevel, onAction, onUpdate }) {
  const [retention, setRetention] = useState(String(record.retention_payable || 0));
  const [reward, setReward] = useState(String(record.reward_amount || 0));
  const [recovery, setRecovery] = useState(record.recovery_amount != null ? String(record.recovery_amount) : '');
  const [recoveryComment, setRecoveryComment] = useState(record.recovery_comment || '');
  const [savingRecovery, setSavingRecovery] = useState(false);
  const [editingIT, setEditingIT] = useState(false);

  const daysWorked = record.days_worked ?? 0;
  const totalDays = record.total_days_in_month ?? 30;
  const gross = record.gross_salary || 0;
  const proRatedSalary = record.final_payable_salary || 0;
  const itAmount = record.income_tax_amount || 0;
  const retentionAmt = record.retention_payable || 0;
  const rewardAmt = record.reward_amount || 0;
  const recoveryAmt = record.recovery_amount || 0;

  // Notice period total: sum of all months' salary within notice period
  const noticeTotalSalary = noticeSettlement?.total_salary || proRatedSalary;
  const noticeMonths = noticeSettlement?.months || [];
  const bankTransfer = noticeTotalSalary + retentionAmt + rewardAmt - recoveryAmt - itAmount;

  const saveField = async (field, value) => {
    try {
      if (field === 'retention_payable') {
        await salaryAPI.updateRetention(record.id, parseFloat(value) || 0);
      } else if (field === 'recovery_comment') {
        await salaryAPI.updateLOP(record.id, { recovery_comment: value });
      } else {
        await salaryAPI.updateLOP(record.id, { [field]: parseFloat(value) || 0 });
      }
      toast.success('Updated');
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveRecovery = async () => {
    setSavingRecovery(true);
    try {
      await salaryAPI.updateLOP(record.id, {
        recovery_amount: parseFloat(recovery) || 0,
        recovery_comment: recoveryComment,
      });
      toast.success('Recovery saved');
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingRecovery(false); }
  };

  const handleITSave = async (type, amount, pct) => {
    try {
      await salaryAPI.updateLOP(record.id, {
        income_tax_type: type || null,
        income_tax_amount: type === 'fixed' ? amount : 0,
        income_tax_percent: type === 'percent' ? pct : 0,
      });
      toast.success('IT updated');
      setEditingIT(false);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [expanded, setExpanded] = useState(false);

  return (
    <Card className={`overflow-hidden border-l-4 ${record.is_paid ? 'border-l-emerald-500 bg-emerald-50/20 dark:bg-emerald-950/10' : record.is_finalized ? 'border-l-blue-500' : 'border-l-orange-500'}`} data-testid={`settlement-card-${record.id}`}>
      <CardContent className="p-0">
        {/* Header — always visible, click to expand */}
        <div
          className="flex items-center justify-between px-5 py-3 bg-muted/30 border-b cursor-pointer select-none hover:bg-muted/50 transition-colors"
          onClick={() => setExpanded(prev => !prev)}
          data-testid={`toggle-card-${record.id}`}
        >
          <div className="flex items-center gap-3">
            <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">{record.employee_name || 'Unknown'}</h3>
                <span className="font-mono text-xs text-muted-foreground">({record.employee_code})</span>
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500 text-white">
                  <AlertTriangle className="h-2.5 w-2.5" /> ON NOTICE
                </span>
                {record.is_paid && <span className="flex items-center gap-1 text-emerald-600 font-semibold text-xs"><CheckCircle className="h-3.5 w-3.5" /> PAID</span>}
                {record.is_finalized && !record.is_paid && <span className="flex items-center gap-1 text-blue-600 font-semibold text-xs"><CheckCircle className="h-3.5 w-3.5" /> FINALIZED</span>}
              </div>
              <p className="text-xs text-muted-foreground">{record.organization_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Notice: {record.notice_start_date || 'N/A'}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> LWD: {record.last_working_date || 'N/A'}</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {daysWorked}/{totalDays} days</span>
            {record.notice_reason && <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{record.notice_reason}</span>}
            <span className={`font-mono font-bold text-sm ${bankTransfer >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {fmt(bankTransfer)}
            </span>
          </div>
        </div>

        {/* Collapsible body */}
        {expanded && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x">
          {/* Column 1: Previous Month */}
          <div className="p-4 space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Previous Month ({prevMonthLabel})</h4>
            {prevMonth ? (
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Gross Salary</span><span className="font-mono">{fmt(prevMonth.gross_salary)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">LOP Days</span><span className="font-mono">{prevMonth.lop_days || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Net Paid</span><span className="font-mono font-semibold">{fmt(prevMonth.bank_transfer)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Status</span>
                  <span className={`text-xs font-medium ${prevMonth.is_paid ? 'text-emerald-600' : prevMonth.is_finalized ? 'text-blue-600' : 'text-amber-600'}`}>
                    {prevMonth.is_paid ? 'Paid' : prevMonth.is_finalized ? 'Finalized' : 'Pending'}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No record for previous month</p>
            )}
          </div>

          {/* Column 2: Current Month Pro-rated */}
          <div className="p-4 space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Current Month ({currentMonthLabel})</h4>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Gross Salary</span><span className="font-mono">{fmt(gross)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Retention Bonus</span><span className="font-mono text-amber-600">-{fmt(record.retention_amount || 0)}</span></div>
              <div className="flex justify-between border-t pt-1 mt-1"><span className="text-muted-foreground font-medium">Net Eligible</span><span className="font-mono font-medium">{fmt(record.net_eligible_salary || (gross - (record.retention_amount || 0)))}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Days Worked</span>
                <span className="font-mono font-semibold text-orange-600">{daysWorked} / {totalDays}</span>
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">LOP Days</span><span className="font-mono text-red-600">{record.lop_days || 0}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Absent Deduction</span><span className="font-mono text-red-600">-{fmt(record.lop_deduction)}</span></div>
              <div className="flex justify-between border-t pt-1.5 mt-1.5">
                <span className="font-medium">Pro-rated Salary</span>
                <span className="font-mono font-bold">{fmt(proRatedSalary)}</span>
              </div>
            </div>
          </div>

          {/* Column 3: Settlement Breakdown */}
          <div className="p-4 space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Settlement Breakdown</h4>
            <div className="space-y-2 text-sm">
              {/* Month-wise salary breakdown from notice period */}
              {noticeMonths.length > 1 ? (
                <div className="space-y-1 rounded-md bg-muted/30 p-2">
                  {noticeMonths.map((m) => (
                    <div key={`${m.month}-${m.year}`} className="flex justify-between">
                      <span className="text-muted-foreground text-xs">{months[m.month - 1]?.label} {m.year}{m.is_resigned ? ' (pro-rated)' : ''}</span>
                      <span className="font-mono text-xs">{fmt(m.salary)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t pt-1 mt-1">
                    <span className="font-medium">Total Notice Salary</span>
                    <span className="font-mono font-bold">{fmt(noticeTotalSalary)}</span>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Pro-rated Salary</span>
                  <span className="font-mono">{fmt(proRatedSalary)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">+ Reward</span>
                {canEdit ? (
                  <Input type="number" min="0" className="w-24 h-7 text-right text-sm font-mono" value={reward}
                    onChange={(e) => setReward(e.target.value)}
                    onBlur={() => saveField('reward_amount', reward)}
                    data-testid={`reward-${record.id}`}
                  />
                ) : <span className="font-mono">{fmt(rewardAmt)}</span>}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">+ Retention Payable</span>
                {canEdit ? (
                  <Input type="number" min="0" className="w-24 h-7 text-right text-sm font-mono" value={retention}
                    onChange={(e) => setRetention(e.target.value)}
                    onBlur={() => saveField('retention_payable', retention)}
                    data-testid={`retention-${record.id}`}
                  />
                ) : <span className="font-mono">{fmt(retentionAmt)}</span>}
              </div>
              <div className="space-y-1.5 rounded-md border border-dashed border-muted-foreground/30 p-2 mt-1">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">- Recovery Amount</span>
                  {canEdit ? (
                    <Input type="number" min="0" placeholder="0" className="w-24 h-7 text-right text-sm font-mono" value={recovery}
                      onChange={(e) => setRecovery(e.target.value)}
                      data-testid={`recovery-${record.id}`}
                    />
                  ) : <span className="font-mono text-red-600">{recoveryAmt ? fmt(recoveryAmt) : '-'}</span>}
                </div>
                {canEdit && (
                  <Input placeholder="Reason (e.g. Laptop, Advance, Notice shortfall)" className="h-7 text-xs" value={recoveryComment}
                    onChange={(e) => setRecoveryComment(e.target.value)}
                    data-testid={`recovery-comment-${record.id}`}
                  />
                )}
                {!canEdit && recoveryComment && (
                  <p className="text-xs text-muted-foreground italic">{recoveryComment}</p>
                )}
                {canEdit && (
                  <Button size="sm" variant="outline" className="h-6 text-xs w-full" onClick={saveRecovery} disabled={savingRecovery} data-testid={`save-recovery-${record.id}`}>
                    {savingRecovery ? 'Saving...' : 'Save Recovery'}
                  </Button>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">- Income Tax</span>
                {canEdit ? (
                  <Popover open={editingIT} onOpenChange={setEditingIT}>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 text-xs font-mono px-2" data-testid={`it-${record.id}`}>
                        {itAmount ? fmt(itAmount) : 'Set IT'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64">
                      <ITEditForm record={record} onSave={handleITSave} />
                    </PopoverContent>
                  </Popover>
                ) : <span className="font-mono text-red-600">{fmt(itAmount)}</span>}
              </div>
              <div className="flex justify-between items-center border-t-2 border-dashed pt-2 mt-2">
                <span className={`font-bold ${bankTransfer >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>= Bank Transfer</span>
                <span className={`font-mono font-bold text-lg ${bankTransfer >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{fmt(bankTransfer)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Follow-up Comments */}
        <SettlementComments recordId={record.id} />

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-5 py-3 bg-muted/20 border-t">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={record.is_finalized}
                disabled={(!record.is_finalized && !canFinalize()) || (record.is_finalized && !canEditFinalized())}
                onCheckedChange={() => onAction(record.is_finalized ? 'unfinalize' : 'finalize')}
                data-testid={`finalize-${record.id}`}
              />
              Finalized
            </label>
            {bankTransfer < 0 ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-300 dark:bg-red-950 dark:text-red-300" data-testid={`do-not-pay-settlement-${record.id}`}>
                <Ban className="h-3 w-3" />
                DO NOT PAY
              </span>
            ) : (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={record.is_paid}
                disabled={(!record.is_paid && (!record.is_finalized || !canMarkPaid())) || (record.is_paid && !canEditFinalized())}
                onCheckedChange={() => onAction(record.is_paid ? 'reverse' : 'paid')}
                data-testid={`paid-${record.id}`}
              />
              Paid
            </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdminLevel() && (!record.is_paid || canEditFinalized()) && (
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => onAction('delete')} data-testid={`delete-${record.id}`}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
          </div>
        </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
